import { supabase } from './supabase'
import { logActivity } from './activity'

/** Canonical DB value for the unassigned/holding column (drag-displace + writes). */
export const UNASSIGNED_CREW_VALUE = 'Blank'

/** User-facing label for the unassigned/holding column (calendar + print). */
export const UNASSIGNED_CREW_LABEL = 'Unassigned'

const UNASSIGNED_ALIASES = new Set(['blank', 'unassigned'])

/** True for null, empty/whitespace, "Blank", or "Unassigned". */
export function isUnassignedCrew(name) {
  if (name == null) return true
  const trimmed = String(name).trim()
  if (!trimmed) return true
  return UNASSIGNED_ALIASES.has(trimmed.toLowerCase())
}

/** Map any unassigned alias to the canonical stored value (`Blank`). */
export function normalizeCrewName(name) {
  return isUnassignedCrew(name) ? UNASSIGNED_CREW_VALUE : name
}

/** Map any unassigned alias to the UI/print label (`Unassigned`). */
export function displayCrewName(name) {
  return isUnassignedCrew(name) ? UNASSIGNED_CREW_LABEL : name
}

/** True when both names belong to the same calendar column. */
export function sameCrewColumn(a, b) {
  if (isUnassignedCrew(a) && isUnassignedCrew(b)) return true
  return a === b
}

/** True when a job's crew_name belongs in the given calendar/print column. */
export function crewMatchesColumn(jobCrew, columnName) {
  if (isUnassignedCrew(columnName)) return isUnassignedCrew(jobCrew)
  return jobCrew === columnName
}

/** True when a job should be included in a selected-crews print filter. */
export function jobMatchesSelectedCrews(job, selectedCrews) {
  if (!selectedCrews) return true
  if (isUnassignedCrew(job.crew_name)) {
    return selectedCrews.has(UNASSIGNED_CREW_LABEL) || selectedCrews.has(UNASSIGNED_CREW_VALUE)
  }
  return selectedCrews.has(job.crew_name)
}

/**
 * Get all crew members (including inactive and deleted)
 */
export async function getCrew() {
  const { data, error } = await supabase
    .from('crew')
    .select('id, name, active, sort_order, color')
    .eq('is_deleted', false)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true })

  if (error) {
    console.error('Error fetching crew:', error)
    return []
  }

  return data || []
}

/**
 * Get only active crew members
 */
export async function getActiveCrew() {
  const { data, error } = await supabase
    .from('crew')
    .select('id, name, active, sort_order, color')
    .eq('is_deleted', false)
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true })

  if (error) {
    console.error('Error fetching active crew:', error)
    return []
  }

  return data || []
}

/**
 * Add a new crew member
 */
export async function addCrewMember(name) {
  // Get the highest sort_order
  const { data: existing } = await supabase
    .from('crew')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)

  const nextSortOrder = existing && existing.length > 0 ? existing[0].sort_order + 1 : 1

  const { data, error } = await supabase
    .from('crew')
    .insert([{
      name: name.trim(),
      active: true,
      sort_order: nextSortOrder
    }])
    .select()

  if (error) {
    console.error('Error adding crew member:', error)
    throw error
  }

  if (data && data.length > 0) {
    await logActivity('crew_created', 'crew', data[0].id, `Added crew member: ${name}`)
  }

  return await getCrew()
}

/**
 * Delete a crew member (soft delete)
 */
export async function deleteCrewMember(id) {
  const { data: crew } = await supabase
    .from('crew')
    .select('name')
    .eq('id', id)
    .single()

  const { error } = await supabase
    .from('crew')
    .update({ is_deleted: true, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) {
    console.error('Error deleting crew member:', error)
    throw error
  }

  if (crew) {
    await logActivity('crew_deleted', 'crew', id, `Deleted crew member: ${crew.name}`)
  }

  return await getCrew()
}

/**
 * Toggle crew member active status
 */
export async function toggleCrewMember(id) {
  // First get the current state
  const { data: current } = await supabase
    .from('crew')
    .select('active, name')
    .eq('id', id)
    .single()

  if (!current) {
    throw new Error('Crew member not found')
  }

  const newActiveState = !current.active

  const { error } = await supabase
    .from('crew')
    .update({ active: newActiveState, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) {
    console.error('Error toggling crew member:', error)
    throw error
  }

  await logActivity('crew_updated', 'crew', id, `${newActiveState ? 'Activated' : 'Deactivated'} crew member: ${current.name}`)

  return await getCrew()
}

/**
 * Rename a crew member.
 */
export async function renameCrewMember(id, newName) {
  const trimmed = newName.trim()

  const { data: current } = await supabase
    .from('crew')
    .select('name')
    .eq('id', id)
    .single()

  if (!current) throw new Error('Crew member not found')

  const oldName = current.name

  // Rename the crew member
  const { error: crewError } = await supabase
    .from('crew')
    .update({ name: trimmed, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (crewError) {
    console.error('Error renaming crew member:', crewError)
    throw crewError
  }

  // Update all jobs that reference the old name so the calendar doesn't lose them
  const { error: jobsError } = await supabase
    .from('jobs')
    .update({ crew_name: trimmed })
    .eq('crew_name', oldName)

  if (jobsError) {
    console.error('Error updating jobs after crew rename:', jobsError)
    // Non-fatal: crew was renamed successfully; log but don't throw
  }

  await logActivity('crew_updated', 'crew', id, `Renamed crew member: ${oldName} → ${trimmed}`)

  return await getCrew()
}

/**
 * Set a crew member's color (hex string like '#3b82f6' or null to clear).
 */
export async function setCrewColor(id, color) {
  const { data: current } = await supabase
    .from('crew')
    .select('name')
    .eq('id', id)
    .single()

  const { error } = await supabase
    .from('crew')
    .update({ color, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) {
    console.error('Error setting crew color:', error)
    throw error
  }

  if (current) {
    await logActivity('crew_updated', 'crew', id, `Set color for crew member: ${current.name}`)
  }

  return await getCrew()
}
